import { buildLoginHrefFromWindow } from '../../utils/returnUrl';
import { clearReadCache } from './readCache';
import { recordFormNeedsSession, waitForSessionResume } from '../sessionResume';

// Determine the API URL based on environment
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is explicitly set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production (when not on localhost), use the current origin
  if (typeof window !== 'undefined') {
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname === '';
    
    if (!isLocalhost) {
      // In production, use the same origin (the app and API are on the same domain)
      return window.location.origin;
    }
  }
  
  // Default to localhost for development
  return 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();
let baseURL = API_BASE_URL;
const PRODUCTION_API_URL = 'https://portal.oneblueox.com'; // Production API URL

/**
 * @param {string} url
 * @param {import('./apiTypes').ApiRequestOptions} [options]
 * @returns {Promise<Response>}
 */
export function sessionFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: 'include'
  });
}

/** Set REACT_APP_API_DEBUG=true to log full request/response details. */
const API_VERBOSE = process.env.REACT_APP_API_DEBUG === 'true';

const isExpectedPermissionError = (status, message) => {
  if (status === 403) return true;
  const msg = String(message || '').toLowerCase();
  return (
    msg.includes('access denied') ||
    msg.includes('not enabled') ||
    msg.includes('not authorized')
  );
};

const logApiVerbose = (...args) => {
  if (API_VERBOSE) console.log(...args);
};

const logApiError = (endpoint, method, status, data, response) => {
  const message = data?.message || response?.statusText || 'Request failed';
  if (isExpectedPermissionError(status, message)) {
    logApiVerbose(`API ${method} ${endpoint} permission denied (${status}): ${message}`);
    return;
  }

  console.warn(`API ${method} ${endpoint} failed (${status}): ${message}`);

  if (API_VERBOSE || status >= 500) {
    if (data?.sqlQuery) console.warn('SQL Query:', data.sqlQuery);
    if (data?.debug) console.warn('API debug:', data.debug);
    else if (API_VERBOSE) console.warn('Response:', data);
  }
};

export function getHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

/**
 * @param {string} endpoint
 * @param {import('./apiTypes').ApiRequestOptions} [options]
 * @returns {Promise<import('./apiTypes').ApiResponse>}
 */
export async function productionRequest(endpoint, options = {}) {
    const url = `${PRODUCTION_API_URL}${endpoint}`;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Ensure we don't have double slashes in the URL
    const endpointUrl = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    try {
      // Ensure we don't have double /api in the URL
      const apiUrl = url.startsWith('/api') ? `${PRODUCTION_API_URL}${url}` : `${PRODUCTION_API_URL}/api${endpointUrl}`;
      console.log('Production API Request Details:', {
        url: apiUrl,
        method: config.method || 'GET',
        headers: config.headers,
        body: config.body ? 'Present' : 'None'
      });
      
      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutes timeout
      
      const response = await sessionFetch(apiUrl, {
        ...config,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log('Production API Response Details:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      const data = await response.json();
      console.log('Production API Response Data:', {
        success: data.success,
        hasData: !!data.data,
        dataLength: Array.isArray(data.data) ? data.data.length : (data.data ? Object.keys(data.data).length : 0),
        message: data.message,
        fullData: data
      });

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Production API Request Error:', error);
      throw error;
    }
  }

/**
 * @param {string} endpoint
 * @param {import('./apiTypes').ApiRequestOptions} [options]
 * @returns {Promise<import('./apiTypes').ApiResponse>}
 */
export async function request(endpoint, options = {}, allowSessionRetry = true) {
    // Ensure baseURL is properly set
    if (!baseURL) {
      baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }
    
    // Normalize the endpoint
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const method = options.method || 'GET';
    
    // Construct the URL ensuring no double slashes
    const url = `${baseURL.replace(/\/+$/, '')}/api${normalizedEndpoint}`;
    
    logApiVerbose('API Request Debug:', {
      baseURL: baseURL,
      endpoint: endpoint,
      normalizedEndpoint: normalizedEndpoint,
      finalUrl: url
    });
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      logApiVerbose('API Request Details:', {
        url: url,
        method: method,
        headers: config.headers,
        body: config.body ? 'Present' : 'None'
      });
      
      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutes timeout
      
      if (API_VERBOSE && url.includes('/compliance') && config.method === 'POST') {
        logApiVerbose('=== API REQUEST BODY DEBUG ===', {
          url,
          method: config.method,
          body: config.body,
        });
      }
      
      const response = await sessionFetch(url, {
        ...config,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      logApiVerbose('API Response Details:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        logApiError(normalizedEndpoint, method, response.status, data, response);
        
        // Handle authentication errors (401) - auto logout
        // Note: 403 errors are permission errors, not authentication errors
        // They should be handled by the calling code, not trigger auto-logout
        if (response.status === 401 && normalizedEndpoint !== '/auth/login') {
          const path = typeof window !== 'undefined' ? window.location.pathname : '';
          const stayOnPublicAuthPage =
            path === '/login' ||
            path === '/forgot-password' ||
            path === '/reset-password';
          if (!stayOnPublicAuthPage) {
            if (allowSessionRetry && recordFormNeedsSession()) {
              try {
                await waitForSessionResume();
              } catch (resumeError) {
                throw resumeError;
              }
              return request(endpoint, options, false);
            }
            console.warn('API authentication error (401); redirecting to login');
            clearReadCache();
            sessionFetch(`${baseURL}/api/auth/logout`, { method: 'POST' }).catch(() => {});
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('auth-session');
            window.location.href = buildLoginHrefFromWindow();
          }
        }
        
        // Create error with response data preserved
        const error = new Error(data.message || `HTTP error! status: ${response.status}`);
        const apiError = /** @type {Error & { response: { data: import('./apiTypes').ApiFailure, status: number, statusText: string }, data: import('./apiTypes').ApiFailure }} */ (error);
        apiError.response = { data, status: response.status, statusText: response.statusText };
        apiError.data = data;
        throw apiError;
      }

      return data;
    } catch (error) {
      if (error.sessionCancelled) throw error;
      if (error.name === 'AbortError') {
        throw new Error('Timeout: Request failed to complete in 120000ms');
      }
      if (!error.response) {
        console.warn(`API ${method} ${normalizedEndpoint} network error:`, error.message);
      }
      throw error;
    }
  }

logApiVerbose('ApiService initialized with baseURL:', baseURL);

export function getBaseUrl() {
  return baseURL;
}

export function setBaseUrl(value) {
  baseURL = value;
}
